import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemIcon,
    Dialog, DialogTitle, DialogContent, TextField, DialogActions, Table,
    TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Grid, Card, CardContent, CardActions,
    Select, MenuItem, FormControl, InputLabel, IconButton, LinearProgress, Checkbox,
    Accordion, AccordionSummary, AccordionDetails, Chip, Avatar, Divider, Tooltip, CircularProgress,
    ListItemSecondaryAction, Stepper, Step, StepLabel, StepButton, Autocomplete, ListItemAvatar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventIcon from '@mui/icons-material/Event';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import CommentIcon from '@mui/icons-material/Comment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningIcon from '@mui/icons-material/Warning';
import ForumIcon from '@mui/icons-material/Forum';
import FolderIcon from '@mui/icons-material/Folder';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function ITProjectDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [countries, setCountries] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [chatMessage, setChatMessage] = useState('');
    const [newTask, setNewTask] = useState('');
    const [newTaskDate, setNewTaskDate] = useState('');
    const [newTaskAssignee, setNewTaskAssignee] = useState(''); // ID of assigned user
    const [addingTask, setAddingTask] = useState(false);

    // Task Comment State
    const [openCommentDialog, setOpenCommentDialog] = useState(false);
    const [currentTask, setCurrentTask] = useState(null);
    const [taskComment, setTaskComment] = useState('');

    // Edit Mode State
    const [editOpen, setEditOpen] = useState(false);
    const [editData, setEditData] = useState({});

    // Needs/Requirements State
    const [needOpen, setNeedOpen] = useState(false);
    const [newNeed, setNewNeed] = useState({ description: '', priority: 'Medium', due_date: '' });
    const [openNeedCommentDialog, setOpenNeedCommentDialog] = useState(false);
    const [currentNeed, setCurrentNeed] = useState(null);
    const [needComment, setNeedComment] = useState('');

    // Assign Member State
    const [assignOpen, setAssignOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [assignRole, setAssignRole] = useState('Member'); // Role for new member
    const [assigningMember, setAssigningMember] = useState(false);

    // File Upload State
    const fileInputRef = React.useRef(null);
    const [uploading, setUploading] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchProject();
        fetchCountries();
        fetchClients();
        fetchUsers();
    }, [id]);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/users`, { headers });
            setUsers(res.data);
        } catch (e) { console.error(e); }
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API_URL}/setup/countries`, { headers });
            setCountries(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchClients = async () => {
        try {
            const res = await axios.get(`${API_URL}/clients`, { headers });
            setClients(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchProject = async () => {
        try {
            const res = await axios.get(`${API_URL}/it-projects/${id}?t=${new Date().getTime()}`, { headers });
            setProject(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleSendMessage = async () => {
        if (!chatMessage.trim()) return;
        try {
            await axios.post(`${API_URL}/it-projects/${id}/chat`, { message: chatMessage }, { headers });
            setChatMessage('');
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const handleAddTask = async () => {
        if (!newTask.trim()) return;
        setAddingTask(true);
        try {
            await axios.post(`${API_URL}/it-projects/${id}/tasks`, {
                title: newTask,
                assigned_to: newTaskAssignee || null,
                due_date: newTaskDate || null
            }, { headers });
            setNewTask('');
            setNewTaskDate('');
            setNewTaskAssignee('');
            fetchProject();
        } catch (e) {
            console.error(e);
        } finally {
            setAddingTask(false);
        }
    }

    const handleToggleTask = async (taskId, currentInfo) => {
        await axios.put(`${API_URL}/it-projects/tasks/${taskId}`, { completed: !currentInfo }, { headers });
        fetchProject();
    }

    const handleUpdateTaskDate = async (taskId, newDate) => {
        try {
            await axios.put(`${API_URL}/it-projects/tasks/${taskId}`, { due_date: newDate }, { headers });
            fetchProject();
        } catch (e) { console.error(e); }
    }

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;
        try {
            await axios.delete(`${API_URL}/it-projects/tasks/${taskId}`, { headers });
            fetchProject();
        } catch (e) { console.error(e); }
    };

    const handleOpenCommentDialog = (task) => {
        setCurrentTask(task);
        setOpenCommentDialog(true);
    };

    const handleSendTaskComment = async () => {
        if (!taskComment.trim()) return;
        try {
            await axios.post(`${API_URL}/it-projects/tasks/${currentTask.id}/comments`, { comment: taskComment }, { headers });
            setTaskComment('');
            fetchProject();
            // Ideally update local state to show new comment immediately or re-fetch just the task?
            // For now, re-fetching project is fine but might close the dialog if we depend on project state? 
            // Actually we are using `currentTask` which is a snapshot. We should update currentTask with new comment if we want to show it?
            // But our dialog will use `project.tasks.find(...)` to be reactive.
        } catch (e) { console.error(e); }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'primary';
            case 'In Progress': return 'primary';
            case 'Completed': return 'success';
            case 'On Hold': return 'warning';
            default: return 'default';
        }
    };

    const calculateProgress = (tasks) => {
        if (!tasks || tasks.length === 0) return 0;
        const completed = tasks.filter(t => t.completed).length;
        return Math.round((completed / tasks.length) * 100);
    };

    const handleEditClick = () => {
        if (editOpen) {
            handleEditSave();
        } else {
            setEditData({
                name: project.name,
                description: project.description,
                status: project.status,
                priority: project.priority,
                country_id: project.country_id || '',
                client_id: project.client_id || '',
                client_id: project.client_id || '',
                time_limit: project.time_limit ? project.time_limit.split('T')[0] : '',
                start_date: project.start_date ? project.start_date.split('T')[0] : ''
            });
            setEditOpen(true);
        }
    };

    const handleEditClose = () => {
        setEditOpen(false);
    };

    const handleEditSave = async () => {
        try {
            await axios.put(`${API_URL}/it-projects/${id}`, editData, { headers });
            setEditOpen(false);
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const handleEditChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const handleAssignMember = async () => {
        if (!selectedUser) return;
        setAssigningMember(true);
        try {
            await axios.post(`${API_URL}/it-projects/${id}/team`, {
                action: 'add',
                user_id: selectedUser.id,
                role: assignRole
            }, { headers });
            setAssignOpen(false);
            setSelectedUser(null);
            setAssignRole('Member');
            fetchProject();
        } catch (e) {
            console.error(e);
            alert('Error assigning member');
        } finally {
            setAssigningMember(false);
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!window.confirm('Remove this member?')) return;
        try {
            await axios.post(`${API_URL}/it-projects/${id}/team`, {
                action: 'remove',
                user_id: userId
            }, { headers });
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            await axios.post(`${API_URL}/it-projects/${id}/documents`, formData, { headers });
            fetchProject();
        } catch (error) {
            console.error(error);
            alert('Failed to upload file');
        } finally {
            setUploading(false);
            if(fileInputRef.current) fileInputRef.current.value = '';
        }
    };
    
    // Download/Delete Helper functions
    const handleDownloadDocument = async (doc) => {
        try {
            const response = await axios.get(`${API_URL}/it-projects/documents/${doc.id}`, {
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error("Download failed", e);
            alert('Failed to download document');
        }
    };

    const handleDeleteDocument = async (docId) => {
        if(!window.confirm("Are you sure you want to delete this document?")) return;
        try {
            await axios.delete(`${API_URL}/it-projects/documents/${docId}`, { headers });
            fetchProject();
        } catch (e) {
            console.error(e);
            alert('Failed to delete document');
        }
    };

    const handleAddNeed = async () => {
        try {
            await axios.post(`${API_URL}/it-projects/${id}/needs`, newNeed, { headers });
            setNeedOpen(false);
            setNewNeed({ description: '', priority: 'Medium', due_date: '' });
            fetchProject();
        } catch (e) {
            console.error(e);
            alert('Error adding requirement');
        }
    };

    const handleDeleteNeed = async (needId) => {
        if (!window.confirm('Delete this requirement?')) return;
        try {
            await axios.delete(`${API_URL}/it-projects/needs/${needId}`, { headers });
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const handleToggleNeedStatus = async (need) => {
        const newStatus = need.status === 'Met' ? 'Pending' : 'Met';
        try {
            await axios.put(`${API_URL}/it-projects/needs/${need.id}`, { status: newStatus }, { headers });
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const handleOpenNeedComments = (need) => {
        setCurrentNeed(need);
        setOpenNeedCommentDialog(true);
        setNeedComment('');
    };

    const handleSendNeedComment = async () => {
        if (!currentNeed || !needComment.trim()) return;
        try {
            await axios.post(`${API_URL}/it-projects/needs/${currentNeed.id}/comments`, { comment: needComment }, { headers });
            setNeedComment('');
            fetchProject();
            // Update local currentNeed comments? fetchProject updates project state, but currentNeed is separate state.
            // Better to refind need from project state or just let next render update it if we used ID.
            // But currentNeed is an object. We should rely on project state for rendering dialog list if possible, or update currentNeed.
            // Let's just update currentNeed manually for now to see immediate result if we don't refetch or if refetch is slow.
            // Actually, since we fetchProject(), the project state updates. If we use project.needs.find... in the dialog, it will auto update.
        } catch (e) {
            console.error(e);
        }
    };

    const handleUpdateNeedComment = async (needId, comment) => {
        try {
            await axios.put(`${API_URL}/it-projects/needs/${needId}`, { comment }, { headers });
            fetchProject();
        } catch (e) {
            console.error(e);
        }
    };

    const steps = ['Open', 'In Progress', 'Completed'];
    const activeStep = steps.indexOf(project?.status) !== -1 ? steps.indexOf(project?.status) : 0;

    const handleStepClick = async (stepLabel) => {
        // Allow updating status by clicking steps? Or just visuals?
        // Let's allow update if desired, or at least show it.
        // For now, let's just use it as visual aid in overview, maybe interactively.
        if (!window.confirm(`Change status to ${stepLabel}?`)) return;
        try {
            await axios.put(`${API_URL}/it-projects/${id}`, { status: stepLabel }, { headers });
            fetchProject();
        } catch (e) { console.error(e); }
    };

    const handleExportPDF = async () => {
        try {
            const response = await axios.get(`${API_URL}/it-projects/${id}/export-pdf`, {
                headers,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Project_Report_${project.ref_number || id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error("Export failed", e);
            const msg = e.response?.data?.error || 'Failed to export PDF';
            try {
                // If blob response is JSON error
                if (e.response?.data instanceof Blob) {
                    const text = await e.response.data.text();
                    const json = JSON.parse(text);
                    if (json.error) alert(json.error);
                    else alert(msg);
                } else {
                    alert(msg);
                }
            } catch {
                alert(msg);
            }
        }
    };

    if (loading) return <Loading />;
    if (!project) return <Typography>Project not found</Typography>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header Section */}
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', color: 'white' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ width: '100%' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/it-projects')}
                            sx={{ color: 'white', mb: 2, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Back to IT Projects
                        </Button>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2 }}>
                            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '3rem' } }}>{project.name}</Typography>
                            <Chip label={project.status} sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                <PublicIcon fontSize="small" />
                                <Typography>{project.country_name || 'Global'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                <AssignmentIcon fontSize="small" />
                                <Typography>{project.ref_number}</Typography>
                            </Box>
                            {project.created_by && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <PersonIcon fontSize="small" />
                                    <Typography>Created by: {project.created_by}</Typography>
                                </Box>
                            )}
                            {project.created_at && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.1)', px: 2, py: 1, borderRadius: 2 }}>
                                    <AccessTimeIcon fontSize="small" />
                                    <Typography>Created on: {new Date(project.created_at).toLocaleDateString()}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                        <Button
                            onClick={handleExportPDF}
                            variant="contained"
                            size="medium"
                            startIcon={<PictureAsPdfIcon />}
                            sx={{
                                bgcolor: editOpen ? '#4caf50' : 'rgba(255,255,255,0.2)',
                                color: 'white',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                '&:hover': {
                                    bgcolor: editOpen ? '#388e3c' : 'rgba(255,255,255,0.3)'
                                },
                                height: 48,
                                minWidth: 48,
                                px: 0,
                                borderRadius: 3
                            }}
                        >

                        </Button>
                        <Button
                            onClick={handleEditClick}
                            variant="contained"
                            size="medium"
                            sx={{
                                bgcolor: editOpen ? '#4caf50' : 'rgba(255,255,255,0.2)',
                                color: 'white',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                '&:hover': {
                                    bgcolor: editOpen ? '#388e3c' : 'rgba(255,255,255,0.3)'
                                },
                                height: 48,
                                minWidth: 48,
                                px: 0,
                                borderRadius: 3
                            }}
                        >
                            {editOpen ? <SaveIcon /> : <EditIcon />}
                        </Button>
                        {project.qr_code && (
                            <Box sx={{ bgcolor: 'white', p: 0.5, borderRadius: 1 }}>
                                <img src={`data:image/png;base64,${project.qr_code}`} alt="QR" width={60} height={60} />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Grid container spacing={3}>
                {/* Left Column - Main Info */}
                <Grid item xs={12}>
                    <Paper sx={{ width: '100%', mb: 2, borderRadius: 3, overflow: 'hidden' }}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Tab icon={<DescriptionIcon />} iconPosition="start" label="Overview" />
                            <Tab icon={<AssignmentIcon />} iconPosition="start" label="Tasks" />
                            <Tab icon={<PersonAddIcon />} iconPosition="start" label="Members" />
                            <Tab icon={<WarningIcon />} iconPosition="start" label="Linked Tickets" />
                            <Tab icon={<FactCheckIcon />} iconPosition="start" label="Project Needs" />
                            <Tab icon={<FolderIcon />} iconPosition="start" label="Documents" />
                            <Tab icon={<SendIcon />} iconPosition="start" label="Chat" />
                        </Tabs>

                        <TabPanel value={tab} index={0}>
                            <Box sx={{ mb: 4, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Workflow Status</Typography>
                                <Stepper nonLinear activeStep={activeStep} alternativeLabel>
                                    {steps.map((label, index) => (
                                        <Step key={label}>
                                            <StepButton color="inherit" onClick={() => handleStepClick(label)}>
                                                {label}
                                            </StepButton>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DescriptionIcon color="primary" />
                                    <Typography variant="h6" fontWeight="bold">Description</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                {editOpen ? (
                                    <TextField fullWidth multiline rows={4} value={editData.description} onChange={e => handleEditChange('description', e.target.value)} />
                                ) : (
                                    <Typography paragraph color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>{project.description || 'No description provided.'}</Typography>
                                )}
                            </Box>

                            <Grid container spacing={3} sx={{ mt: 2 }}>
                                <Grid item xs={6} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon color="warning" />
                                            <Typography variant="subtitle2" fontWeight="bold">Priority</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <Select fullWidth size="small" value={editData.priority} onChange={e => handleEditChange('priority', e.target.value)}>
                                                <MenuItem value="Low">Low</MenuItem>
                                                <MenuItem value="Medium">Medium</MenuItem>
                                                <MenuItem value="High">High</MenuItem>
                                                <MenuItem value="Critical">Critical</MenuItem>
                                            </Select>
                                        ) : (
                                            <Chip label={project.priority} size="small" variant="filled" color={project.priority === 'High' ? 'error' : 'default'} />
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <EventIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Start Date</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <TextField fullWidth size="small" type="date" value={editData.start_date} onChange={e => handleEditChange('start_date', e.target.value)} />
                                        ) : (
                                            <Typography variant="body2">{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <EventIcon color="action" />
                                            <Typography variant="subtitle2" fontWeight="bold">Due Date</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <TextField fullWidth size="small" type="date" value={editData.time_limit} onChange={e => handleEditChange('time_limit', e.target.value)} />
                                        ) : (
                                            <Typography variant="body2">{project.time_limit ? new Date(project.time_limit).toLocaleDateString() : 'No Deadline'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <AssignmentIcon color="success" />
                                            <Typography variant="subtitle2" fontWeight="bold">Progress</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                            <Box sx={{ width: '100%', mr: 1 }}>
                                                <LinearProgress variant="determinate" value={calculateProgress(project.tasks)} sx={{ height: 8, borderRadius: 5 }} />
                                            </Box>
                                            <Typography variant="body2" fontWeight="bold">{`${calculateProgress(project.tasks)}%`}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <BusinessIcon color="primary" />
                                            <Typography variant="subtitle2" fontWeight="bold">Client</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <Select fullWidth size="small" value={editData.client_id} onChange={e => handleEditChange('client_id', e.target.value)}>
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {clients.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Typography variant="body2">{clients.find(c => c.id === project.client_id)?.name || project.client_name || 'No Client'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PublicIcon color="info" />
                                            <Typography variant="subtitle2" fontWeight="bold">Country</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <Select fullWidth size="small" value={editData.country_id} onChange={e => handleEditChange('country_id', e.target.value)}>
                                                <MenuItem value=""><em>Global</em></MenuItem>
                                                {countries.map(c => (
                                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Typography variant="body2">{project.country_name || 'Global'}</Typography>
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={6} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon color="warning" />
                                            <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                                        </Box>
                                        {editOpen ? (
                                            <Select fullWidth size="small" value={editData.status} onChange={e => handleEditChange('status', e.target.value)}>
                                                <MenuItem value="Active">Active</MenuItem>
                                                <MenuItem value="In Progress">In Progress</MenuItem>
                                                <MenuItem value="Completed">Completed</MenuItem>
                                                <MenuItem value="On Hold">On Hold</MenuItem>
                                            </Select>
                                        ) : (
                                            <Chip label={project.status} size="small" color={getStatusColor(project.status)} />
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={1}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                                <TextField
                                    fullWidth size="small"
                                    placeholder="Add new task..."
                                    value={newTask}
                                    onChange={e => setNewTask(e.target.value)}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label="Due Date"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 150 }}
                                    value={newTaskDate}
                                    onChange={(e) => setNewTaskDate(e.target.value)}
                                />
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Assign To (Optional)</InputLabel>
                                    <Select
                                        value={newTaskAssignee}
                                        label="Assign To (Optional)"
                                        onChange={(e) => setNewTaskAssignee(e.target.value)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {project.team.map(member => (
                                            <MenuItem key={member.id} value={member.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{member.username[0]}</Avatar>
                                                    {member.full_name || member.username}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button variant="contained" onClick={handleAddTask} disabled={addingTask} startIcon={addingTask ? <CircularProgress size={20} /> : <AddIcon />} sx={{ borderRadius: 2, px: 3, height: 40 }}>Add</Button>
                            </Box>

                            <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
                                <CardContent sx={{ p: '0 !important' }}>
                                    <List>
                                        {project.tasks.length === 0 && <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No active tasks.</Typography></Box>}
                                        {project.tasks.map((task, index) => (
                                            <ListItem key={task.id} divider={index !== project.tasks.length - 1} sx={{ py: 2 }}>
                                                <ListItemIcon>
                                                    <Checkbox checked={task.completed} onChange={() => handleToggleTask(task.id, task.completed)} color="primary" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Typography variant="body1" sx={{ textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'text.disabled' : 'text.primary', fontWeight: task.completed ? 'normal' : 'medium' }}>
                                                                    {task.title}
                                                                </Typography>
                                                                {task.assigned_to_name && (
                                                                    <Chip
                                                                        avatar={<Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>{task.assigned_to_name[0]}</Avatar>}
                                                                        label={task.assigned_to_fullname || task.assigned_to_name}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ height: 24, fontSize: '0.75rem', border: 'none', bgcolor: 'grey.100' }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                                {task.created_at && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Created: {new Date(task.created_at).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                                {task.due_date && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Due: {new Date(task.due_date).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            {task.completed && task.completed_at && (
                                                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                                                    Done: {new Date(task.completed_at).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />

                                                <ListItemSecondaryAction>
                                                    <IconButton size="small" onClick={() => handleOpenCommentDialog(task)}>
                                                        <CommentIcon fontSize="small" color={task.comments && task.comments.length > 0 ? "primary" : "action"} />
                                                    </IconButton>
                                                    <IconButton edge="end" size="small" onClick={() => handleDeleteTask(task.id)}><DeleteIcon fontSize="small" /></IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>


                            <Grid container spacing={2}>
                                {project.milestones.map(m => (
                                    <Grid item xs={12} md={6} key={m.id}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderColor: m.completed ? 'success.main' : 'warning.main' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold">{m.title}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{m.due_date ? `Due: ${new Date(m.due_date).toLocaleDateString()}` : 'No deadline'}</Typography>
                                                </Box>
                                                <Chip label={m.completed ? "Done" : "Pending"} color={m.completed ? "success" : "default"} size="small" variant="outlined" />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tab} index={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold">Assigned Members</Typography>
                                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAssignOpen(true)} sx={{ borderRadius: 2 }}>
                                    Assign Member
                                </Button>
                            </Box>
                            <Grid container spacing={2}>
                                {project.team.map(member => (
                                    <Grid item xs={12} sm={6} md={4} key={member.id}>
                                        <Card
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 3,
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'primary.main' }
                                            }}
                                        >
                                            <CardContent sx={{ position: 'relative' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>{member.username[0]}</Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">{member.full_name || member.username}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{member.role}</Typography>
                                                    </Box>
                                                </Box>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                    <IconButton size="small" color="error" onClick={() => handleRemoveMember(member.id)} sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'error.main', color: 'white' } }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {project.team.length === 0 && (
                                    <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                                            <PersonAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                            <Typography variant="h6" color="textSecondary" gutterBottom>No team members yet</Typography>
                                            <Button variant="outlined" onClick={() => setAssignOpen(true)}>Assign Someone</Button>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>

                        </TabPanel>

                        <TabPanel value={tab} index={3}>
                            <Box sx={{ mt: 0 }}>
                                <Typography variant="h6" gutterBottom fontWeight="bold">Linked Tickets</Typography>
                                {project.tickets.length > 0 ? (
                                    <List dense>
                                        {project.tickets.map(ticket => (
                                            <ListItem key={ticket.id} sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                                <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                                                <ListItemText primary={<Typography fontWeight="medium">{ticket.title}</Typography>} secondary={`Status: ${ticket.status}`} />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography color="text.secondary">No linked tickets.</Typography>
                                )}
                            </Box>
                        </TabPanel>

                        <TabPanel value={tab} index={4}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                                <TextField
                                    fullWidth size="small"
                                    placeholder="Add new requirement..."
                                    value={newNeed.description}
                                    onChange={e => setNewNeed({ ...newNeed, description: e.target.value })}
                                    InputProps={{ sx: { borderRadius: 2 } }}
                                />
                                <TextField
                                    type="date"
                                    size="small"
                                    label="Due Date"
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 150 }}
                                    value={newNeed.due_date}
                                    onChange={(e) => setNewNeed({ ...newNeed, due_date: e.target.value })}
                                />
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        value={newNeed.priority}
                                        label="Priority"
                                        onChange={(e) => setNewNeed({ ...newNeed, priority: e.target.value })}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        <MenuItem value="Low">Low</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="High">High</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button variant="contained" onClick={handleAddNeed} startIcon={<AddIcon />} sx={{ borderRadius: 2, px: 3, height: 40 }} disabled={!newNeed.description}>Add</Button>
                            </Box>

                            <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
                                <CardContent sx={{ p: '0 !important' }}>
                                    <List>
                                        {project.needs && project.needs.length === 0 && <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No needs or requirements listed yet.</Typography></Box>}
                                        {project.needs && project.needs.map((need, index) => (
                                            <ListItem key={need.id} divider={index !== (project.needs ? project.needs.length - 1 : 0)} sx={{ py: 2 }}>
                                                <ListItemIcon>
                                                    <Checkbox
                                                        checked={need.status === 'Met'}
                                                        onChange={() => handleToggleNeedStatus(need)}
                                                        color="primary"
                                                    />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Typography variant="body1" sx={{ textDecoration: need.status === 'Met' ? 'line-through' : 'none', color: need.status === 'Met' ? 'text.disabled' : 'text.primary', fontWeight: need.status === 'Met' ? 'normal' : 'medium' }}>
                                                                    {need.description}
                                                                </Typography>
                                                                <Chip
                                                                    label={need.priority}
                                                                    size="small"
                                                                    color={need.priority === 'High' ? 'error' : need.priority === 'Medium' ? 'warning' : 'default'}
                                                                    variant="outlined"
                                                                    sx={{ height: 24, fontSize: '0.75rem', border: 'none', bgcolor: 'grey.100' }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                                                {need.created_at && (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Created: {new Date(need.created_at).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                                {need.due_date ? (
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Due: {new Date(need.due_date).toLocaleDateString()}
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography variant="caption" color="text.disabled">No Deadline</Typography>
                                                                )}
                                                            </Box>
                                                            {need.done_at && (
                                                                <Typography variant="caption" color="success.main" display="block">
                                                                    Completed: {new Date(need.done_at).toLocaleDateString()}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton size="small" onClick={() => handleOpenNeedComments(need)}>
                                                        <CommentIcon fontSize="small" color={need.comments && need.comments.length > 0 ? 'primary' : 'action'} />
                                                    </IconButton>
                                                    {need.comments && need.comments.length > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{need.comments.length}</Typography>
                                                    )}
                                                    <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteNeed(need.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </TabPanel>


                        <TabPanel value={tab} index={5}>
                            <Card sx={{ borderRadius: 3, boxShadow: 0 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <AttachFileIcon color="primary" />
                                        <Typography variant="h6" fontWeight="bold">Documents</Typography>
                                    </Box>
                                    <Divider sx={{ mb: 2 }} />
                                    <List dense>
                                        {project.documents.map(doc => (
                                            <ListItem key={doc.id}
                                                secondaryAction={
                                                    <Box>
                                                        <IconButton edge="end" aria-label="download" onClick={() => handleDownloadDocument(doc)}>
                                                            <CloudUploadIcon /> 
                                                        </IconButton>
                                                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteDocument(doc.id)} color="error">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                }
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: 'info.light' }}><DescriptionIcon /></Avatar>
                                                </ListItemAvatar>
                                                <ListItemText primary={doc.filename} secondary={new Date(doc.uploaded_at).toLocaleDateString()} />
                                            </ListItem>
                                        ))}
                                        {project.documents.length === 0 && <Typography variant="body2" color="text.secondary" align="center">No documents uploaded.</Typography>}
                                    </List>
                                    <input
                                        type="file"
                                        hidden
                                        ref={fileInputRef}
                                        onChange={handleFileUpload}
                                    />
                                    <Button 
                                        variant="outlined" 
                                        fullWidth 
                                        startIcon={uploading ? <CircularProgress size={20} /> : <AttachFileIcon />} 
                                        sx={{ mt: 2, borderRadius: 2 }}
                                        disabled={uploading}
                                        onClick={() => fileInputRef.current.click()}
                                    >
                                        {uploading ? 'Uploading...' : 'Upload File'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </TabPanel>

                        <TabPanel value={tab} index={6}>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 0 }}>
                                    <Box sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                                            <Typography variant="subtitle1" fontWeight="bold">Project Discussion</Typography>
                                            <Typography variant="caption" color="text.secondary">Use this channel for quick updates and team coordination.</Typography>
                                        </Box>

                                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, bgcolor: '#f8fafc' }}>
                                            {project.chats.length === 0 && (
                                                <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.6 }}>
                                                    <ForumIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                                    <Typography variant="body2" color="text.secondary">No messages yet. Start the conversation!</Typography>
                                                </Box>
                                            )}
                                            {project.chats.map((chat, idx) => (
                                                <Box key={chat.id || idx} sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, ml: 0.5 }}>
                                                        <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: 'secondary.main' }}>{chat.user[0]}</Avatar>
                                                        <Typography variant="caption" fontWeight="bold" color="text.primary">{chat.user}</Typography>
                                                        <Typography variant="caption" color="text.secondary">• {new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                                                    </Box>
                                                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'white', borderRadius: '4px 16px 16px 16px', border: '1px solid #e2e8f0', maxWidth: '85%' }}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{chat.message}</Typography>
                                                    </Paper>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider' }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="Type your message..."
                                                    value={chatMessage}
                                                    onChange={e => setChatMessage(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                                    InputProps={{ sx: { borderRadius: 3, bgcolor: 'grey.50' } }}
                                                />
                                                <IconButton
                                                    color="primary"
                                                    onClick={handleSendMessage}
                                                    disabled={!chatMessage.trim()}
                                                    sx={{
                                                        bgcolor: chatMessage.trim() ? 'primary.main' : 'action.disabledBackground',
                                                        color: 'white',
                                                        width: 40, height: 40,
                                                        '&:hover': { bgcolor: 'primary.dark' },
                                                        '&.Mui-disabled': { color: 'white' }
                                                    }}
                                                >
                                                    <SendIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </TabPanel>
                    </Paper >
                </Grid >
            </Grid >


            {/* Add Need Dialog */}
            < Dialog open={needOpen} onClose={() => setNeedOpen(false)} maxWidth="sm" fullWidth >
                <DialogTitle>Add Requirement</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Description/Need"
                        value={newNeed.description}
                        onChange={e => setNewNeed({ ...newNeed, description: e.target.value })}
                        multiline rows={2}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={newNeed.priority}
                            label="Priority"
                            onChange={e => setNewNeed({ ...newNeed, priority: e.target.value })}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Due Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={newNeed.due_date}
                        onChange={e => setNewNeed({ ...newNeed, due_date: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNeedOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddNeed} variant="contained" disabled={!newNeed.description}>Add</Button>
                </DialogActions>
            </Dialog >

            {/* Task Comment Dialog */}
            < Dialog open={openCommentDialog} onClose={() => setOpenCommentDialog(false)} maxWidth="sm" fullWidth >
                <DialogTitle>
                    Task Comments
                    <Typography variant="caption" display="block" color="text.secondary">{currentTask?.title}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        {project && currentTask && project.tasks.find(t => t.id === currentTask.id)?.comments && project.tasks.find(t => t.id === currentTask.id)?.comments.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No comments yet.</Typography>
                        )}
                        {project && currentTask && project.tasks.find(t => t.id === currentTask.id)?.comments?.map(comment => (
                            <Box key={comment.id} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">{comment.user}</Typography>
                                    <Typography variant="caption" color="text.secondary">{new Date(comment.timestamp).toLocaleString()}</Typography>
                                </Box>
                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2">{comment.comment}</Typography>
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a comment..."
                        value={taskComment}
                        onChange={(e) => setTaskComment(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCommentDialog(false)}>Close</Button>
                    <Button onClick={handleSendTaskComment} variant="contained" disabled={!taskComment.trim()} endIcon={<SendIcon />}>Send</Button>
                </DialogActions>
            </Dialog >

            {/* Need Comment Dialog */}
            < Dialog open={openNeedCommentDialog} onClose={() => setOpenNeedCommentDialog(false)} maxWidth="sm" fullWidth >
                <DialogTitle>
                    Requirement Comments
                    <Typography variant="caption" display="block" color="text.secondary">{currentNeed?.description}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                        {project && currentNeed && project.needs.find(n => n.id === currentNeed.id)?.comments && project.needs.find(n => n.id === currentNeed.id)?.comments.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>No comments yet.</Typography>
                        )}
                        {project && currentNeed && project.needs.find(n => n.id === currentNeed.id)?.comments?.map(comment => (
                            <Box key={comment.id} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="subtitle2" fontWeight="bold">{comment.user}</Typography>
                                    <Typography variant="caption" color="text.secondary">{new Date(comment.timestamp).toLocaleString()}</Typography>
                                </Box>
                                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                    <Typography variant="body2">{comment.comment}</Typography>
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Add a comment..."
                        value={needComment}
                        onChange={(e) => setNeedComment(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNeedCommentDialog(false)}>Close</Button>
                    <Button onClick={handleSendNeedComment} variant="contained" disabled={!needComment.trim()} endIcon={<SendIcon />}>Send</Button>
                </DialogActions>
            </Dialog >

            {/* Assign Member Dialog */}
            < Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth >
                <DialogTitle>Assign Team Member</DialogTitle>
                <DialogContent sx={{ overflow: 'visible' }}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            options={users.filter(u => !project.team.some(m => m.id === u.id))}
                            getOptionLabel={(option) => `${option.full_name || option.username} (${option.role || 'User'})`}
                            value={selectedUser}
                            onChange={(event, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => <TextField {...params} label="Search User" variant="outlined" />}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Project Role</InputLabel>
                            <Select
                                value={assignRole}
                                label="Project Role"
                                onChange={(e) => setAssignRole(e.target.value)}
                            >
                                <MenuItem value="Manager">Manager</MenuItem>
                                <MenuItem value="Lead">Lead</MenuItem>
                                <MenuItem value="Developer">Developer</MenuItem>
                                <MenuItem value="Tester">Tester</MenuItem>
                                <MenuItem value="Designer">Designer</MenuItem>
                                <MenuItem value="Viewer">Viewer</MenuItem>
                                <MenuItem value="Member">Member</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
                    <Button onClick={handleAssignMember} variant="contained" disabled={!selectedUser || assigningMember} startIcon={assigningMember ? <CircularProgress size={20} /> : null}>Assign</Button>
                </DialogActions>
            </Dialog >
        </Container >
    );
}

export default ITProjectDetails;
